import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { request } from '../api/client';
import { Alert, Badge, Spinner } from '../shared/components/feedback';
import { Button } from '../shared/components/Button';
import { useDialog } from '../shared/dialog/DialogProvider';

interface DeletionRequest {
  confirmationCode: string;
  status: string;
  requestedAt: string;
  completedAt?: string | null;
  threadAccountId?: number | null;
}

export function DataDeletionRequestsPanel() {
  const { confirm } = useDialog();
  const [requests, setRequests] = useState<DeletionRequest[]>([]);
  const [reviewed, setReviewed] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    request<DeletionRequest[]>('/api/thread-data-deletions')
      .then((data) => { if (!cancelled) setRequests(data); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : '삭제 요청을 불러오지 못했습니다.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refresh]);
  const complete = async (item: DeletionRequest) => {
    if (!reviewed[item.confirmationCode] || busy) return;
    const ok = await confirm({ title: '계정 데이터 영구 삭제', message: `요청 ${item.confirmationCode}의 계정 데이터와 연결된 기록을 영구 삭제합니다. 외부 파일과 백업 검토를 마쳤으면 진행하세요.`, confirmText: '영구 삭제하고 완료', cancelText: '취소', tone: 'danger' });
    if (!ok) return;
    setBusy(item.confirmationCode);
    setError(null);
    try {
      await request(`/api/thread-data-deletions/${encodeURIComponent(item.confirmationCode)}/complete`, { method: 'POST', body: { externalAssetsAndBackupsReviewed: true } });
      setReviewed((old) => ({ ...old, [item.confirmationCode]: false }));
      setRefresh((value) => value + 1);
    } catch (err) { setError(err instanceof Error ? err.message : '삭제 처리를 완료하지 못했습니다.'); }
    finally { setBusy(null); }
  };
  return (
    <Panel aria-label="데이터 삭제 요청">
      <PanelHeader>
        <div>
          <PanelTitle>데이터 삭제 요청</PanelTitle>
          <Help>접수된 요청의 처리 상태를 확인하고 계정 데이터를 관리합니다.</Help>
        </div>
        <Button $variant="secondary" disabled={loading || !!busy} onClick={() => setRefresh((n) => n + 1)}>새로고침</Button>
      </PanelHeader>
      {error && <Alert tone="danger">{error}</Alert>}
      {loading ? (
        <StateMessage role="status"><Spinner aria-hidden="true" />삭제 요청을 불러오는 중입니다.</StateMessage>
      ) : !requests.length && !error ? (
        <StateMessage>접수된 삭제 요청이 없습니다.</StateMessage>
      ) : requests.map((item) => (
        <RequestCard key={item.confirmationCode} aria-busy={busy === item.confirmationCode}>
          <RequestHeader>
            <strong>{item.threadAccountId != null ? `계정 #${item.threadAccountId}` : '삭제된 계정'}</strong>
            <Badge $tone={item.status === 'DELETED' ? 'success' : item.status === 'REQUESTED' ? 'warning' : 'neutral'}>
              {item.status === 'DELETED' ? '삭제 완료' : item.status === 'REQUESTED' ? '처리 대기' : item.status}
            </Badge>
          </RequestHeader>
          <RequestMeta>
            <span>요청 번호 <RequestCode>{item.confirmationCode}</RequestCode></span>
            <span>접수일 {item.requestedAt?.replace('T', ' ').slice(0, 16) || '확인 중'}</span>
            {item.completedAt && <span>완료일 {item.completedAt.replace('T', ' ').slice(0, 16)}</span>}
          </RequestMeta>
          {item.status === 'REQUESTED' && (
            <ReviewArea>
              <ReviewLabel>
                <input type="checkbox" checked={!!reviewed[item.confirmationCode]} disabled={!!busy} onChange={(event) => setReviewed((old) => ({ ...old, [item.confirmationCode]: event.target.checked }))} />
                <span>해당 계정의 외부 저장 파일·공용 자산·백업을 확인하고 필요한 삭제 및 보존 처리를 마쳤습니다.</span>
              </ReviewLabel>
              <RequestActions>
                <Help>삭제한 계정 데이터와 기록은 복구할 수 없습니다.</Help>
                <Button $variant="danger" disabled={!reviewed[item.confirmationCode] || !!busy} onClick={() => void complete(item)}>
                  {busy === item.confirmationCode ? '삭제 처리 중…' : '계정 데이터 영구 삭제'}
                </Button>
              </RequestActions>
            </ReviewArea>
          )}
        </RequestCard>
      ))}
    </Panel>
  );
}

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 6px;
`;

const Help = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
  line-height: 1.6;
`;

const StateMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 26px 16px;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
`;

const RequestCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  padding: 18px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const RequestHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 15px;
`;

const RequestMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 20px;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
  overflow-wrap: anywhere;
`;

const RequestCode = styled.span`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;
`;

const ReviewArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: 14px;
`;

const ReviewLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-height: 44px;
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  font-size: 14px;
  line-height: 1.6;
  cursor: pointer;

  input {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 3px;
    accent-color: ${({ theme }) => theme.colors.primary};
  }
`;

const RequestActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 480px) {
    button { width: 100%; }
  }
`;
